import React from "react";
import { motion } from "framer-motion";

export default function HomePage() {
  return (
    <main className="font-sans text-gray-800">
      {/* Hero Section */}
      <motion.section
        className="min-h-screen bg-gradient-to-r from-[#004d29] to-[#00C569] flex flex-col-reverse md:flex-row items-center justify-center text-white text-center md:text-left p-8 gap-8"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div>
          <img src="images/logo.png" alt="Logo" className="w-48 h-auto mx-auto md:mx-0 mb-4 shadow-md" />
          <h1 className="text-5xl font-bold mt-10 mb-4">MSME by G3G</h1>
          <p className="text-xl mb-6">Sell smarter. Shop locally. Myanmar’s SME marketplace made digital.</p>
          <div className="space-x-4 mt-12">
            <button className="bg-white text-green-600 font-semibold px-6 py-3 rounded-xl shadow hover:scale-105 transition-transform">Download App</button>
            <button className="border border-white px-6 py-3 rounded-xl hover:bg-white hover:text-green-600 transition-colors">Learn More</button>
          </div>
        </div>
        <motion.img
          src="images/3d_phone_tilt.png"
          alt="App Preview"
          className="w-[480px] h-auto"
          animate={{ y: [0, -20, 0] }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.section>

      {/* Our Story */}
      <motion.section
        className="p-10 bg-gradient-to-r from-white to-[#f3fff6] flex flex-col md:flex-row items-center gap-10"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <motion.img
          src="images/1.png"
          alt="Our Story"
          className="w-[640px] h-auto rounded-xl"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        />
        <div className="text-center md:text-left">
          <h2 className="text-3xl font-bold mb-4">Our Story</h2>
          <p className="max-w-xl">
            Born during the COVID-19 crisis, MSME by G3G collaborated with over 200 SME businesses across Myanmar, selling products online via Facebook. We now introduce a full-fledged E-commerce platform to take it to the next level.
          </p>
        </div>
      </motion.section>

      {/* What is MSME App */}
      <motion.section
        className="p-10 bg-[#f0fdf5] flex flex-col md:flex-row-reverse items-center gap-10"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <img src="images/phone_2d_straight.png" alt="What is MSME App" className="w-[520px] h-auto rounded-xl shadow-md" />
        <div className="text-center md:text-left">
          <h2 className="text-3xl font-bold mb-4">What is MSME App?</h2>
          <p className="max-w-xl">
            A multi-shop E-commerce platform that brings diverse local shops together into a single digital marketplace. Our goal: Make shopping easier and help small businesses grow online.
          </p>
        </div>
      </motion.section>

      {/* How It Works */}
      <motion.section
        className="p-10 bg-white text-center"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <h2 className="text-3xl font-bold mb-8">How It Works</h2>
        <motion.div
          className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          transition={{ staggerChildren: 0.2 }}
          variants={{ hidden: {}, visible: {} }}
        >
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              className="bg-[#f3fff6] p-6 rounded-xl shadow-md"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: i * 0.2 }}
            >
              <h3 className="text-xl font-semibold mb-2">{["1. Browse Shops", "2. Place Orders", "3. Track & Receive"][i]}</h3>
              <p>
                {
                  [
                    "Explore local sellers across categories and find unique products.",
                    "Add to cart and checkout easily with secure payment options.",
                    "Track orders in real time and enjoy fast delivery to your doorstep.",
                  ][i]
                }
              </p>
            </motion.div>
          ))}
        </motion.div>
      </motion.section>

      {/* Seller & Buyer Benefits */}
      <motion.section
        className="py-10 px-4 bg-gradient-to-r from-white to-[#e8fff2] flex flex-col md:flex-row items-center gap-10"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <img src="images/stores_mm.png" alt="Benefits" className="w-[680px] h-auto rounded-xl shadow-lg" />
        <div className="text-center md:text-left">
          <h2 className="text-3xl font-bold mb-4">Benefits for Everyone</h2>
          <motion.div
            className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto text-left"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            transition={{ staggerChildren: 0.2 }}
            variants={{ hidden: {}, visible: {} }}
          >
            {["For Sellers", "For Buyers"].map((role, i) => (
              <motion.div
                key={role}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: i * 0.2 }}
              >
                <h3 className="text-xl font-semibold mb-2">{role}</h3>
                <div className="space-y-3">
                  {(i === 0
                    ? ["Easy product uploads", "Better visibility and digital reach"]
                    : ["Fair pricing", "Direct access to authentic products"]
                  ).map((item, j) => (
                    <div key={j} className="flex items-center space-x-3">
                      <span className="text-green-600 text-lg">✔</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Key Features */}
      <motion.section
        className="py-10 px-4 bg-[#f1fff6] flex flex-col md:flex-row-reverse items-center gap-10"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <img src="images/products_mm.png" alt="Key Features" className="w-[700px] h-auto rounded-xl shadow-lg" />
        <div className="text-center md:text-left">
          <h2 className="text-3xl font-bold mb-6">Key Features</h2>
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto text-left">
            <div className="space-y-3">
              {["Multi-Shop Marketplace", "User-Friendly Interface", "Advanced Search & Filters", "Secure Payment System"].map((feature, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <span className="text-green-600 text-lg">✔</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
            <div className="space-y-3">
              {["Fast & Free Delivery", "Real-Time Order Tracking", "Promotions & Loyalty Points", "Product Recommendations"].map((feature, i) => (
                <div key={i} className="flex items-center space-x-3">
                  <span className="text-green-600 text-lg">✔</span>
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.section>

      {/* Final CTA */}
      <motion.section
        className="min-h-[50vh] bg-gradient-to-r from-[#00C569] to-[#4CAF50] text-white flex flex-col md:flex-row items-center justify-between p-10 gap-10"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        viewport={{ once: true }}
      >
        <div className="text-center md:text-left">
          <h2 className="text-3xl font-bold mb-4">Ready for a Better Shopping Experience?</h2>
          <p className="mb-6 max-w-xl">
            MSME by G3G brings you closer to trusted SMEs with a seamless and secure e-commerce platform.
          </p>
          <button className="bg-white text-green-600 font-semibold px-8 py-3 rounded-xl shadow hover:scale-105 transition-transform">Get Started</button>
        </div>
        <motion.img
          src="images/products_jacket_mm.png"
          alt="Call to Action"
          className="w-[640px] h-auto rounded-xl shadow-md"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        />
      </motion.section>

      {/* Add Privacy Policy Link */}
      <div className="text-center py-6">
        <a href="/privacy-policy" className="text-white underline">Privacy Policy</a>
      </div>
    </main>
  );
}
